import React, { useState } from 'react';
import axios from 'axios';
import { Heart, Sparkles, Coffee, Check } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { images } from '../mock/mock';
import { useContent } from '../context/ContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const perks = [
  {
    icon: Coffee,
    title: 'A team that feels like family',
    body: 'Work alongside passionate people in a warm, welcoming St Kilda kitchen and café.',
  },
  {
    icon: Sparkles,
    title: 'Real craft, real produce',
    body: 'Learn the art of small-batch nut milks and genuinely good food made from scratch.',
  },
  {
    icon: Heart,
    title: 'Room to grow',
    body: 'We love to see people develop, front of house, kitchen, production and beyond.',
  },
];

export default function Careers() {
  const [form, setForm] = useState({ name: '', email: '', role: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  useReveal([]);
  const { content, txt } = useContent();
  const jobs = content.jobs || [];

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        business: form.role,
        message: form.message,
        type: 'careers',
      });
      setSent(true);
      setForm({ name: '', email: '', role: '', message: '' });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('careers enquiry failed', err);
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* hero, hidden; remove the spacer above and "hidden" below to reinstate */}
      <section className="hidden relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
        <img
          src={images.careersHeader}
          alt="The Al Naturale hospitality team"
          className="absolute inset-0 w-full h-full object-cover object-[center_20%] opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
            Careers
          </span>
          <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
            Join the family
          </h1>
          <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
            We're always on the lookout for warm, curious people who care about good
            food and doing things the natural way.
          </p>
        </div>
      </section>

      {/* intro + perks */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal max-w-3xl">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              {txt('careers', 'introHeading', 'Grow with Al Naturale')}
            </h2>
            <p className="mt-5 text-[#2C3A28] text-lg leading-relaxed">
              From our little St Kilda café to our small-batch production kitchen, our
              team is the heart of everything we make. We don't always have a role listed
              publicly, but the right person is always worth hearing from. Send us a hello
              and we'll let you know about current openings.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {perks.map((p) => (
              <div
                key={p.title}
                className="reveal rounded-3xl bg-[#EDEAE0] p-8"
                data-testid={`career-perk-${p.title.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
              >
                <span className="inline-flex w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] items-center justify-center">
                  <p.icon size={22} />
                </span>
                <h3 className="mt-5 font-display text-2xl font-semibold text-[#2C3A28]">{p.title}</h3>
                <p className="mt-3 text-[#2C3A28] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* current openings */}
      {jobs.length > 0 && (
        <section className="pb-4" data-testid="careers-openings">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <h2 className="font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28] leading-tight">
              Current openings
            </h2>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {jobs.map((j, i) => (
                <div key={j.id || i} className="reveal rounded-3xl bg-white p-7" data-testid={`job-listing-${i}`}>
                  <h3 className="font-display text-2xl font-semibold text-[#2C3A28]">{j.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold uppercase tracking-wide text-[#2C3A28]/70">
                    {j.type && <span>{j.type}</span>}
                    {j.location && <span>{j.location}</span>}
                  </div>
                  {j.description && <p className="mt-3 text-[#2C3A28] leading-relaxed">{j.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* apply */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal rounded-[2rem] bg-[#2C3A28] text-[#E9EBED] px-8 sm:px-14 py-14 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center overflow-hidden relative grain">
            <div className="relative">
              <Heart size={38} className="text-[#EDEAE0]" />
              <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#E9E5D8]">
                {txt('careers', 'applyHeading', 'Apply to find out current openings')}
              </h2>
              <p className="mt-4 max-w-xl text-white text-lg leading-relaxed">
                Tell us a little about yourself and the kind of role you're after, barista,
                kitchen, front of house or production. We'll be in touch about what's
                currently available.
              </p>
            </div>
            <div className="relative">
              {sent ? (
                <div className="rounded-2xl bg-[#E9EBED] text-[#2C3A28] p-8 text-center" data-testid="careers-success">
                  <div className="mx-auto w-12 h-12 rounded-full bg-[#2C3A28] text-[#E9EBED] flex items-center justify-center">
                    <Check size={24} />
                  </div>
                  <p className="mt-4 font-display text-2xl">Grazie!</p>
                  <p className="mt-1 text-[#3B2A20]/70">
                    Thanks for reaching out, we'll let you know about current openings soon.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="careers-send-another"
                    className="mt-5 text-sm font-semibold text-[#2C3A28] hover:text-[#1f2a1c]"
                  >
                    Send another application
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="rounded-2xl bg-[#E9EBED] p-6 sm:p-7 space-y-3" data-testid="careers-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      data-testid="careers-name"
                      className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                    />
                    <input
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      placeholder="Role you're after"
                      data-testid="careers-role"
                      className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                    />
                  </div>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address"
                    data-testid="careers-email"
                    className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors"
                  />
                  <textarea
                    required
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us a little about yourself and your experience..."
                    data-testid="careers-message"
                    className="w-full rounded-xl border border-[#2C3A28]/15 bg-white px-4 py-3 text-[#2C3A28] outline-none focus:border-[#2C3A28] transition-colors resize-none"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="careers-submit"
                    className="w-full rounded-full bg-[#2C3A28] text-[#EDEAE0] py-3.5 font-semibold hover:bg-[#1f2a1c] transition-colors disabled:opacity-60"
                  >
                    {busy ? 'Sending...' : 'Apply now'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
